/**
 * InboxFiltersBar — one action bar for a unified inbox.
 *
 * Presentation is deliberately the same as `PersonalInboxToolbar`: search, a
 * counted Filters popover, a Newest/Oldest toggle, the result count and one
 * Sync button. The two surfaces sit behind the same mailbox switcher, so a bar
 * that reads differently on each side makes the switcher feel like it changed
 * applications rather than changed inbox.
 *
 * That is a reversal of the note this file used to carry ("deliberately NOT a
 * change to InboxFiltersBar"). What that note was protecting is still true and
 * still enforced: the two bars share no state, no credentials and no scope.
 * They now share a LAYOUT, and each keeps its own vocabulary — the unified bar
 * filters on `replied` and sorts by SORT_KEYS keys (`received_at`,
 * `sender_email`) applied client-side, where the personal bar filters on
 * `unread`/`provider` and sorts server-side by `sort_by`. Folding those two
 * vocabularies into one component is what the old note was warning against.
 *
 * State lives in the URL query string, not component state. An admin chasing an
 * unreplied backlog shares the link, reloads after a Graph hiccup, or opens a
 * thread and comes back; all three lose the filter set if it lives in memory. It
 * also makes the filters a bookmarkable view rather than a per-session mode.
 *
 * `mailbox` is one comma-joined list of SMTP addresses and IS bookmarkable:
 * it encodes BOTH the Distro pill and the member chips — a distro address
 * alone reads as "Distro", any other combination as the chips themselves.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
    Check,
    Search,
    X,
} from 'lucide-react';

import { Button } from '../ui/button';
import {
    ComposeButton,
    InboxCountLabel,
    InboxFilterButton,
    InboxSettingsButton,
    InboxSortMenu,
    SyncButton,
} from './inboxControls';
import { unifiedSort } from './mailSortOptions';
import { Input } from '../ui/input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../ui/popover';
import MailboxFilterSelect from './MailboxFilterSelect';
import { useSyncByMailbox } from '../../hooks/useSyncProgress';
import {
    useInboxMailboxes,
    useMailboxAccessRequests,
} from '../../hooks/useUnifiedInbox';
import { connectionsFromRefs } from '../../utils/inboxMailboxes';

/* The sort vocabulary moved to `mailSortOptions.js`, shared with the personal
   bar. The values it writes are still SORT_KEYS keys — the same keys the column
   headers emit, so the menu and a header click go through one vocabulary. The
   retired select here used to write `sortBy: 'comm_date' | 'from_email'`, the
   PERSONAL inbox's parameter names: no consumer of this bar read `sortBy`, so
   two of its three options were inert and the third never arrived. Keeping one
   table for both lanes is what stops that recurring. */

const STATUSES = [
    { key: 'all', label: 'All' },
    { key: 'unreplied', label: 'Needs reply' },
    { key: 'replied', label: 'Replied' },
];

/** Which status pill is lit. `replied` is tri-state: absent, false, true. */
function activeStatus(filters) {
    if (filters.replied === false) return 'unreplied';
    if (filters.replied === true) return 'replied';
    return 'all';
}

/** The flag a status pill sets. Only one status is meaningful at a time. */
function statusPatch(key) {
    if (key === 'unreplied') return { replied: false };
    if (key === 'replied') return { replied: true };
    return { replied: undefined };
}

/** The mailbox filter is one comma-joined string; these are its two ends. */
function mailboxList(filters) {
    return (filters.mailbox || '').split(',').map(s => s.trim()).filter(Boolean);
}

function selectedFromMailbox(filters, members) {
    const addresses = mailboxList(filters);
    const known = new Map(
        members.map(m => [String(m.email || '').trim().toLowerCase(), m]),
    );
    return addresses.map(a =>
        known.get(a.toLowerCase()) || { email: a, name: null, picture: null },
    );
}

export default function InboxFiltersBar({
    filters,
    onChange,
    resultCount,
    totalCount,
    /** §11.4's control strip: Compose is the strip's violet primary. */
    onCompose,
    onSettings,
    onSync,
    isSyncing = false,
    inboxId = null,
    distroSmtp = null,
    // Issue #662: threaded through to MailboxFilterSelect's "Manage mailboxes"
    // footer. Absent on both = no button (admins keep /admin/unified-inbox).
    canManage = false,
    onManageMailboxes,
}) {
    const [localSearch, setLocalSearch] = useState(filters.search || '');
    const [filtersOpen, setFiltersOpen] = useState(false);
    // Display-name cache for the mailbox chips. MailboxFilterSelect renders a
    // member ROW and can call `onKnown(m)` with it; this bar keeps those rows
    // so a chip can carry a name/avatar even after a reload, when the URL only
    // holds the address. Never reset on inbox switch — a chip for an address
    // this inbox no longer contains still renders better as a name than as a
    // raw SMTP string.
    const [knownMembers, setKnownMembers] = useState([]);
    const {
        data: mailboxData,
        isLoading: mailboxesLoading,
        isError: mailboxesError,
    } = useInboxMailboxes(inboxId);
    const { data: microsoftRequests = [] } = useMailboxAccessRequests('microsoft', inboxId);
    const { data: googleRequests = [] } = useMailboxAccessRequests('google', inboxId);
    const pendingPermissions = useMemo(
        () => [...microsoftRequests, ...googleRequests].filter(request =>
            request?.status === 'pending' || request?.status === 'sent'
        ),
        [microsoftRequests, googleRequests],
    );
    // Which pooled mailboxes are still importing their history, keyed by the
    // address the option rows are keyed by. A mailbox added to an existing
    // inbox backfills in the background, and until this existed a half-imported
    // mailbox was indistinguishable from a quiet one.
    //
    // CONSUMED from the page's provider, never polled here. This used to call
    // `useSyncProgress` itself, and a second React Query observer on the same
    // key gets its own `setInterval` — two out-of-phase 2s timers on one lane.
    const syncByMailbox = useSyncByMailbox();

    const connectedMailboxes = useMemo(
        () => (mailboxData ? connectionsFromRefs(mailboxData) : null),
        [mailboxData],
    );

    // Debounce search input — 300ms before propagating to parent
    useEffect(() => {
        const timer = setTimeout(() => {
            if (localSearch !== (filters.search || '')) {
                onChange({ ...filters, search: localSearch || undefined, page: 1 });
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [localSearch]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync local state when external filter changes (e.g., Clear button)
    useEffect(() => {
        if (filters.search !== localSearch && filters.search !== undefined) {
            setLocalSearch(filters.search || '');
        }
    }, [filters.search]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = patch => onChange({ ...filters, ...patch, page: 1 });

    // Search is excluded: it has its own always-visible box, so counting it
    // here would show "1 filter" for something the user can already see.
    const activeCount =
        (filters.sender ? 1 : 0) +
        (filters.dateFrom || filters.dateTo ? 1 : 0) +
        (activeStatus(filters) !== 'all' ? 1 : 0) +
        (filters.starred ? 1 : 0) +
        (filters.hasAttachment ? 1 : 0) +
        // The Distro pills AND the member chips are one filter: any
        // non-empty mailbox list counts once.
        (filters.mailbox ? 1 : 0);

    // The Distro pill is lit only when the mailbox list is EXACTLY the distro
    // address — distro plus a member chip is a compound selection that is
    // neither pill, and lighting Distro then would lie about what is filtered.
    const mailboxAddresses = mailboxList(filters);
    const distroOnly =
        Boolean(distroSmtp) && mailboxAddresses.length === 1 && mailboxAddresses[0] === distroSmtp;

    const clearAll = () => {
        setLocalSearch('');
        onChange({ page: 1, pageSize: filters.pageSize });
    };

    return (
        <div
            className="px-4 py-2 border-b border-stone-100 flex items-center gap-2"
            data-testid="inbox-filters-bar"
        >
            {onCompose && <ComposeButton onClick={onCompose} />}

            {/* The current unified inbox's connected mailboxes sit beside
                Compose, not behind Filters. This is the same controlled
                MailboxFilterSelect used below, but it consumes the already
                scoped connection rows rather than the member directory. Its
                changes still write through `set` and therefore the shared URL
                codec; the popover remains available for Distro and the other
                filters. */}
            {inboxId && (
                <div
                    className="w-[190px] min-w-[170px] max-w-[190px] flex-shrink-0"
                    data-testid="inbox-mailbox-dropdown"
                >
                    <MailboxFilterSelect
                        inboxId={inboxId}
                        mailboxes={connectedMailboxes}
                        syncByMailbox={syncByMailbox}
                        mailboxesLoading={mailboxesLoading}
                        mailboxesError={mailboxesError}
                        selected={selectedFromMailbox(filters, knownMembers)}
                        onChange={members =>
                            set({
                                mailbox: members.map(m => m.email).join(',') || undefined,
                            })
                        }
                        onKnown={m =>
                            setKnownMembers(prev => {
                                const email = String(m.email || '').trim().toLowerCase();
                                return prev.some(
                                    x => String(x.email || '').trim().toLowerCase() === email,
                                )
                                    ? prev
                                    : [...prev, { ...m, email }];
                            })
                        }
                        disabled={!inboxId}
                        canManage={canManage}
                        onManageMailboxes={onManageMailboxes}
                        pendingPermissions={pendingPermissions}
                    />
                </div>
            )}

            {/* Search */}
            <div className="relative flex-1 min-w-[180px] max-w-md">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                <Input
                    value={localSearch}
                    onChange={e => setLocalSearch(e.target.value)}
                    placeholder="Search mail…"
                    className="pl-8 h-8 text-sm bg-stone-50 border-stone-200"
                    data-testid="inbox-filter-search"
                    aria-label="Search inbox"
                />
                {localSearch && (
                    <button
                        type="button"
                        onClick={() => setLocalSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                        aria-label="Clear search"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            <div className="flex-1" />

            <InboxCountLabel
                shown={typeof resultCount === 'number' ? resultCount : undefined}
                total={totalCount}
                testId="inbox-result-count"
            />

            {/* Filter · Sort · Sync · Settings — the same four icon buttons the
                personal bar ends with, in the same order. Both lanes draw one
                control strip; only the popover CONTENT differs, because only
                the filter vocabularies differ. */}
            <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
                <PopoverTrigger asChild>
                    <InboxFilterButton
                        activeCount={activeCount}
                        open={filtersOpen}
                        testId="inbox-filters-trigger"
                    />
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 p-4 space-y-4 overflow-hidden">
                    {/* Status — one choice, so pills rather than two toggles
                        that could both be pressed into a contradiction. The
                        two testids are the ones the old "Unreplied only" /
                        "Replied only" buttons carried; the control moved, the
                        thing it does did not. */}
                    <div className="space-y-1.5">
                        <p className="text-xs font-medium text-stone-500">Status</p>
                        <div className="flex flex-wrap gap-1.5">
                            {STATUSES.map(s => {
                                const isActive = activeStatus(filters) === s.key;
                                return (
                                    <button
                                        key={s.key}
                                        type="button"
                                        onClick={() =>
                                            set(
                                                isActive
                                                    ? statusPatch('all')
                                                    : statusPatch(s.key)
                                            )
                                        }
                                        className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                                            isActive
                                                ? 'bg-stone-900 text-white'
                                                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                        }`}
                                        data-testid={
                                            s.key === 'all'
                                                ? 'inbox-filter-status-all'
                                                : `inbox-filter-${s.key}`
                                        }
                                    >
                                        {s.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Starred / Has attachment — §11.4's Filter-by rows.
                        Independent toggles rather than pills: unlike Status they
                        compose with each other and with everything else. Both map
                        onto real `MailPredicate` fields, so a bulk action over the
                        filtered view acts on exactly these rows — unlike Status
                        and Mailbox, which cannot be carried into a predicate and
                        therefore withhold the "select all N" escalation. */}
                    <div className="space-y-1.5">
                        <p className="text-xs font-medium text-stone-500">Show only</p>
                        <div className="flex flex-wrap gap-1.5">
                            <button
                                type="button"
                                onClick={() =>
                                    set({ starred: filters.starred ? undefined : true })
                                }
                                aria-pressed={Boolean(filters.starred)}
                                data-testid="inbox-filter-starred"
                                className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                                    filters.starred
                                        ? 'bg-stone-900 text-white'
                                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                }`}
                            >
                                Starred
                            </button>
                            <button
                                type="button"
                                onClick={() =>
                                    set({
                                        hasAttachment: filters.hasAttachment ? undefined : true,
                                    })
                                }
                                aria-pressed={Boolean(filters.hasAttachment)}
                                data-testid="inbox-filter-attachment"
                                className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                                    filters.hasAttachment
                                        ? 'bg-stone-900 text-white'
                                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                }`}
                            >
                                Has attachment
                            </button>
                        </div>
                    </div>

                    {/* Mailbox — receiving-mailbox filter (delivery truth). The All/Distro
                        pills only; `All` is the absence of the URL param and `Distro` selects
                        the inbox's distro_smtp alone. Labeled "Mailbox" rather than the issue's
                        "From" so it cannot be confused with the sender-side From input below.

                        The member multi-select that used to sit under these pills is GONE. It
                        was the same control as the switcher beside Compose, writing the same
                        `mailbox` param through the same codec — and the comment at that call
                        site says the mailboxes "sit beside Compose, not behind Filters", so one
                        of the two was always meant to go. Scoping this copy (#703 cause 2) is
                        what made them identical rather than merely redundant.

                        Gated on `inboxId` for the same reason the switcher is: with no inbox
                        selected there is no distro and no member list, so the section would
                        render two dead pills. */}
                    {inboxId && (
                    <div className="space-y-1.5" data-testid="inbox-filter-mailbox-section">
                        <p className="text-xs font-medium text-stone-500">Mailbox</p>
                        <div className="flex flex-wrap gap-1.5">
                            {/* Same pill JSX as the Status section above — the
                                bar's pill grammar is one pattern, and a second
                                dialect would read as a second control kind.
                                `distroOnly` is the Distro pill's active test:
                                the list must be EXACTLY [distroSmtp], so adding
                                a member chip to a distro selection correctly
                                lights nothing rather than both pills. */}
                            <button
                                type="button"
                                // All is the ABSENCE of the param, so its handler
                                // is one clear in both states — matching the
                                // Status pills, where clicking the active pill
                                // also falls through to statusPatch('all').
                                onClick={() => set({ mailbox: undefined })}
                                className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                                    mailboxAddresses.length === 0
                                        ? 'bg-stone-900 text-white'
                                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                }`}
                                data-testid="inbox-filter-mailbox-all"
                            >
                                All
                            </button>
                            <button
                                type="button"
                                onClick={() =>
                                    distroOnly
                                        ? set({ mailbox: undefined })
                                        : set({ mailbox: distroSmtp || undefined })
                                }
                                disabled={!distroSmtp}
                                className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                                    distroOnly
                                        ? 'bg-stone-900 text-white'
                                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                } ${!distroSmtp ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={!distroSmtp ? 'No shared mailbox on this inbox' : undefined}
                                data-testid="inbox-filter-mailbox-distro"
                            >
                                Distro
                            </button>
                        </div>
                    </div>
                    )}

                    {/* Sender */}
                    <div className="space-y-1.5">
                        <label
                            className="text-xs font-medium text-stone-500"
                            htmlFor="inbox-filter-sender"
                        >
                            From
                        </label>
                        <Input
                            id="inbox-filter-sender"
                            value={filters.sender || ''}
                            onChange={e => set({ sender: e.target.value || undefined })}
                            placeholder="Name or email…"
                            className="h-8 text-sm bg-stone-50 border-stone-200"
                            data-testid="inbox-filter-sender"
                        />
                    </div>

                    {/* Date range */}
                    <div className="space-y-1.5">
                        <p className="text-xs font-medium text-stone-500">Received</p>
                        <div className="flex flex-col gap-1.5">
                            <Input
                                type="date"
                                value={filters.dateFrom || ''}
                                onChange={e => set({ dateFrom: e.target.value || undefined })}
                                className="h-8 text-xs bg-stone-50 border-stone-200 w-full"
                                data-testid="inbox-filter-date-from"
                                aria-label="Received from"
                            />
                            <Input
                                type="date"
                                value={filters.dateTo || ''}
                                onChange={e => set({ dateTo: e.target.value || undefined })}
                                className="h-8 text-xs bg-stone-50 border-stone-200 w-full"
                                data-testid="inbox-filter-date-to"
                                aria-label="Received until"
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between pt-1 border-t border-stone-100">
                        <button
                            type="button"
                            onClick={clearAll}
                            className="text-xs text-stone-500 hover:text-stone-800"
                            data-testid="inbox-filter-clear"
                        >
                            Clear all
                        </button>
                        <Button
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => setFiltersOpen(false)}
                        >
                            <Check className="w-3.5 h-3.5 mr-1" />
                            Done
                        </Button>
                    </div>
                </PopoverContent>
            </Popover>

            {/* The sort menu pins the KEY alongside the direction: the default
                view carries no key at all (the server's own `comm_date`
                ordering), so a direction with nothing to apply it to would be a
                control that visibly does nothing. */}
            <InboxSortMenu
                value={unifiedSort.activeKey(filters)}
                onChange={(key) => set(unifiedSort.patch(key))}
                testId="inbox-sort"
            />

            {/* Sync sits in the bar rather than in a strip of its own above the
                banners, which is where the unified inbox's bare refresh icon
                used to live — three regions away from the controls whose result
                it refreshes. */}
            {onSync && (
                <SyncButton
                    onClick={onSync}
                    isSyncing={isSyncing}
                    testId="inbox-refresh"
                />
            )}

            <InboxSettingsButton onClick={onSettings} />
        </div>
    );
}

/**
 * Read filters out of a URL query string.
 *
 * `replied` is tri-state: absent (any), `false` (unreplied only), `true` (replied
 * only). It is parsed explicitly rather than through Boolean() because
 * `Boolean('false')` is true, which would invert the most-used filter in the page.
 */
export function filtersFromSearch(search) {
    const params = new URLSearchParams(search || '');
    const filters = {
        page: Number(params.get('page') || 1) || 1,
        pageSize: Number(params.get('page_size') || 25) || 25,
    };
    if (params.get('search')) filters.search = params.get('search');
    if (params.get('sender')) filters.sender = params.get('sender');
    if (params.get('date_from')) filters.dateFrom = params.get('date_from');
    if (params.get('date_to')) filters.dateTo = params.get('date_to');
    const replied = params.get('replied');
    if (replied === 'true') filters.replied = true;
    if (replied === 'false') filters.replied = false;
    const starred = params.get('starred');
    if (starred === 'true') filters.starred = true;
    if (starred === 'false') filters.starred = false;
    if (params.get('has_attachment') === 'true') filters.hasAttachment = true;
    if (params.get('sort')) filters.sort = params.get('sort');
    if (params.get('dir')) filters.direction = params.get('dir');
    if (params.get('inbox')) filters.inbox = params.get('inbox');
    // The mailbox filter encodes BOTH the Distro pill and the member chips as
    // one comma-separated list of SMTP addresses. It IS bookmarkable — unlike
    // `page`, which is deliberately never written — so it round-trips here.
    if (params.get('mailbox')) filters.mailbox = params.get('mailbox');
    return filters;
}

/** The inverse — only non-default values are written, so the URL stays readable. */
export function searchFromFilters(filters, extra = {}) {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.sender) params.set('sender', filters.sender);
    if (filters.dateFrom) params.set('date_from', filters.dateFrom);
    if (filters.dateTo) params.set('date_to', filters.dateTo);
    if (filters.replied !== undefined) params.set('replied', String(filters.replied));
    if (filters.starred !== undefined) params.set('starred', String(filters.starred));
    if (filters.hasAttachment) params.set('has_attachment', 'true');
    if (filters.mailbox) params.set('mailbox', filters.mailbox);
    // `page` is deliberately NOT written. How far a reader has scrolled is not a
    // bookmarkable view, and writing it meant a reload — or a shared link —
    // landed on page 7 alone, with the six pages above it missing and nothing
    // on screen saying so. The surface keeps its scroll position in component
    // state; the filters, which ARE worth sharing, stay here.
    //
    // `filtersFromSearch` still READS `page` so an old bookmark decodes without
    // error; the surface ignores it.
    if (filters.pageSize && filters.pageSize !== 25) {
        params.set('page_size', String(filters.pageSize));
    }
    if (filters.sort) params.set('sort', filters.sort);
    if (filters.direction && filters.direction !== 'desc') params.set('dir', filters.direction);
    Object.entries(extra).forEach(([key, value]) => {
        if (value) params.set(key, value);
    });
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}
